
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { EmailMetricsTable } from './EmailMetricsTable';

interface EmailMetricData {
  date: string;
  report_type: 'summary' | 'insights';
  total_sent: number;
  successful_sends: number;
  failed_sends: number;
  success_rate: number;
}

export function EmailMetricsSection() {
  const [emailMetrics, setEmailMetrics] = useState<EmailMetricData[]>([]);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEmailMetrics = async () => {
      setLoading(true);
      setError(null);
      try {
        // Use weekly_reports table to get email metrics data
        const daysAgo = timeRangeToDays(timeRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

        const { data, error } = await supabase
          .from('weekly_reports')
          .select('*')
          .gte('week_start', cutoffDate.toISOString().split('T')[0])
          .order('week_start', { ascending: false })
          .limit(30);

        if (error) throw error;
        
        // Transform data to match expected EmailMetricData format
        const transformedData: EmailMetricData[] = (data || []).map((report: Record<string, unknown>) => ({
          date: String(report.week_start || ''),
          report_type: report.ai_insights ? 'insights' as const : 'summary' as const,
          total_sent: 1, // Placeholder - would need actual email tracking
          successful_sends: (report.email_status as Record<string, unknown>)?.status === 'sent' ? 1 : 0,
          failed_sends: (report.email_status as Record<string, unknown>)?.status === 'failed' ? 1 : 0,
          success_rate: (report.email_status as Record<string, unknown>)?.status === 'sent' ? 100 : 0,
        }));
        
        setEmailMetrics(transformedData);
      } catch (err: unknown) {
        console.error('Error fetching email metrics:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchEmailMetrics();
  }, [timeRange]);

  const timeRangeToDays = (range: '7d' | '30d' | '90d'): number => {
    switch (range) {
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      default: return 30;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Performance Metrics</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={timeRange} onValueChange={(value: string) => setTimeRange(value as '7d' | '30d' | '90d')}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="7d">7 Days</TabsTrigger>
            <TabsTrigger value="30d">30 Days</TabsTrigger>
            <TabsTrigger value="90d">90 Days</TabsTrigger>
          </TabsList>
          <TabsContent value={timeRange}>
            <EmailMetricsTable 
              emailMetrics={emailMetrics}
              loading={loading}
              error={error}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
